/**
 * Carrying an anonymous session's data across a sign-in.
 *
 * Visitors chat and take the quiz before signing in. Some sign-in paths REPLACE
 * the anonymous session rather than upgrading it — a Google credential conflict
 * falls back to `signInWithCredential`, and `loginWithPassword` is a plain
 * sign-in — and everything keyed on the old uid (chats, quiz profile, wallet)
 * is then stranded.
 *
 * The server can move that data, but only against proof that we really owned
 * the session: an ID token minted for it. A bare uid would let anyone name a
 * stranger's session and have its chats moved into their own account.
 *
 * The proof MUST be captured before the sign-in call, because afterwards
 * `auth.currentUser` is the new account and the anonymous session is gone.
 *
 * The magic-link path does not use this: there the browser opening the link
 * usually never had the original session (that is the whole cross-browser bug),
 * so `verifyMagicLink` performs the adoption server-side instead.
 */
import * as Sentry from "@sentry/react";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";
import { logEvent } from "./loggingService";

/** Set when a migration failed, so the chats screen can explain the gap. */
export const SESSION_ADOPTION_FAILED_KEY = "sessionAdoptionFailed";

/** Sign-in must not stall behind a slow migration. */
const DEFAULT_ADOPTION_TIMEOUT_MS = 6000;

/**
 * Whether a migration is currently in flight, plus the chat ids the anonymous
 * session had when it started. `onAuthStateChanged` fires with the new uid the
 * moment sign-in resolves, and the chats listener for that uid comes back
 * empty until the server has moved the chats — a second or more. During that
 * window ChatsPage used to show "chat not found" and unmount the thread, which
 * on remount replayed the whole conversation. ChatsPage reads this to keep a
 * "restoring" state up instead.
 */
type AdoptionInFlight = { sourceUid: string; chatIds: Set<string>; startedAt: number } | null;
let adoptionInFlight: AdoptionInFlight = null;
const adoptionListeners = new Set<() => void>();

function setAdoptionInFlight(next: AdoptionInFlight): void {
  adoptionInFlight = next;
  adoptionListeners.forEach((listener) => listener());
}

/** True while a captured session is being migrated; `chatId` narrows it to a chat the session had. */
export function isSessionAdoptionInFlight(chatId?: string): boolean {
  if (!adoptionInFlight) return false;
  if (!chatId) return true;
  return adoptionInFlight.chatIds.has(chatId);
}

export function subscribeSessionAdoption(listener: () => void): () => void {
  adoptionListeners.add(listener);
  return () => {
    adoptionListeners.delete(listener);
  };
}

/**
 * Marks the window between "sign-in resolved with a different uid" and "the
 * migration finished or timed out". Called by the sign-in paths right before
 * they await `adoptCapturedSession`; cleared by it. `chatIds` are the chats the
 * anonymous session had on screen (from the app state) so a deep link to one of
 * them can be held rather than declared missing.
 */
export function beginSessionAdoption(proof: AnonymousSessionProof | null, chatIds: Iterable<string>): void {
  if (!proof) return;
  setAdoptionInFlight({ sourceUid: proof.uid, chatIds: new Set(chatIds), startedAt: Date.now() });
}

export type AnonymousSessionProof = {
  uid: string;
  idToken: string;
};

export type AdoptionOutcome = {
  status: string;
  chats: number;
};

/**
 * Captures proof of the current anonymous session, if there is one.
 *
 * Call this BEFORE the sign-in attempt — once the session is replaced the token
 * can no longer be minted.
 */
export async function captureAnonymousSessionProof(): Promise<AnonymousSessionProof | null> {
  const currentUser = auth.currentUser;
  if (!currentUser?.isAnonymous) {
    return null;
  }

  try {
    const idToken = await currentUser.getIdToken();
    return { uid: currentUser.uid, idToken };
  } catch (error) {
    console.warn("Failed to capture anonymous session proof:", error);
    return null;
  }
}

/**
 * Asks the server to move the captured session's data onto the account that is
 * now signed in.
 *
 * No-ops when the uid did not actually change (the `linkWithPopup` /
 * `linkWithCredential` upgrade paths keep it, so there is nothing to move).
 * Never throws: sign-in has already succeeded by this point, and a failed
 * migration must not look like a failed sign-in.
 */
export async function adoptCapturedSession(
  proof: AnonymousSessionProof | null,
  options: {
    reason: "google" | "password";
    timeoutMs?: number;
  }
): Promise<AdoptionOutcome | null> {
  if (!proof) {
    return null;
  }

  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.isAnonymous) {
    setAdoptionInFlight(null);
    return null;
  }
  if (currentUser.uid === proof.uid) {
    // The session was upgraded in place — the data never moved.
    setAdoptionInFlight(null);
    return null;
  }

  const adoptFn = httpsCallable<
    { sourceIdToken: string; reason: string },
    AdoptionOutcome
  >(functions, "adoptAnonymousSession");

  try {
    const result = await Promise.race([
      adoptFn({ sourceIdToken: proof.idToken, reason: options.reason }),
      new Promise<never>((_, reject) =>
        window.setTimeout(
          () => reject(new Error("adoption_timeout")),
          options.timeoutMs ?? DEFAULT_ADOPTION_TIMEOUT_MS
        )
      ),
    ]);

    const outcome = result.data;
    void logEvent("session_adopted", {
      method: options.reason,
      status: outcome.status,
      chats: outcome.chats,
    });
    setAdoptionInFlight(null);
    return outcome;
  } catch (error) {
    setAdoptionInFlight(null);
    // A timeout is not necessarily a failure — the migration may still land
    // server-side, and the chats listener will pick it up. Anything else is
    // worth surfacing.
    const isTimeout =
      error instanceof Error && error.message === "adoption_timeout";

    if (!isTimeout) {
      Sentry.withScope((scope) => {
        scope.setTag("error_path", "session_adoption");
        scope.setTag("adoption_reason", options.reason);
        Sentry.captureException(error);
      });
      try {
        window.sessionStorage.setItem(SESSION_ADOPTION_FAILED_KEY, "1");
      } catch {
        // Private-mode storage failures are not worth escalating.
      }
    }

    // Split events so "failed" means failed: a timeout only means the callable
    // exceeded the race above — the migration may still land server-side.
    // (Replaces the single session_adoption_failed {timeout} event, 2026-08-31.)
    if (isTimeout) {
      void logEvent("session_adoption_timeout", { method: options.reason });
    } else {
      void logEvent("session_adoption_failed", { method: options.reason });
    }
    return null;
  }
}

